import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { InputRef } from 'antd';
import { Button, Card, Empty, Flex, Form, Input, InputNumber, Select, Tag, Typography, message } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { formatDate, orDash } from '../../lib/format';
import { createJobCard, searchCounter, type JobType, type SearchHit, type SearchHitUnit } from './api';
import { JOB_STATUS_LABEL, JOB_TYPE_LABEL, jobTone } from './status';

const { Text, Title } = Typography;

const JOB_TYPES: JobType[] = ['SERVICE', 'REPAIR', 'WARRANTY', 'INSURANCE', 'ACCESSORY'];

/**
 * The workshop counter (Appendix A #6).
 *
 * One box. Phone, plate, chassis, customer number or job number all land on the
 * same customer and motorcycle, and NOTHING here creates a customer — the only
 * create is a job card against a record that already exists. That is the whole
 * point: a returning customer not found in four seconds gets typed in again,
 * and then there are two of them with half a service history each.
 */
export function CounterPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const inputRef = useRef<InputRef>(null);
  const [term, setTerm] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [opening, setOpening] = useState<{ hit: SearchHit; unit: SearchHitUnit } | null>(null);
  const [jobType, setJobType] = useState<JobType>('SERVICE');
  const [mileage, setMileage] = useState<number | null>(null);
  const [complaint, setComplaint] = useState('');

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const { data: hits, isFetching } = useQuery({
    queryKey: ['workshop-search', submitted],
    queryFn: () => searchCounter(submitted),
    enabled: submitted.length >= 3,
  });

  const open = useMutation({
    mutationFn: () =>
      createJobCard({
        customerUid: opening?.hit.customerUid ?? '',
        unitUid: opening?.unit.uid ?? '',
        jobType,
        ...(mileage !== null ? { mileageIn: mileage } : {}),
        ...(complaint.trim().length > 0 ? { complaints: [complaint.trim()] } : {}),
      }),
    onSuccess: (job) => {
      void queryClient.invalidateQueries({ queryKey: ['job-cards'] });
      setOpening(null);
      void navigate(`/workshop/job-cards/${job.uid}`);
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('workshop.openFailed'));
    },
  });

  return (
    <Flex vertical gap={16}>
      <Card>
        <Form
          layout="vertical"
          onSubmitCapture={(e) => {
            e.preventDefault();
            setSubmitted(term.trim());
          }}
        >
          <Form.Item
            label={
              <Title level={5} style={{ margin: 0 }}>
                {t('workshop.counterPrompt')}
              </Title>
            }
            help={t('workshop.counterHelp')}
            style={{ marginBottom: 0 }}
          >
            <Input
              ref={inputRef}
              size="large"
              autoFocus
              autoComplete="off"
              style={{ fontSize: 18, height: 52 }}
              value={term}
              placeholder={t('workshop.counterPlaceholder')}
              onChange={(e) => {
                setTerm(e.target.value);
              }}
              onPressEnter={() => {
                setSubmitted(term.trim());
              }}
            />
          </Form.Item>
        </Form>
      </Card>

      {submitted.length >= 3 && !isFetching && (hits ?? []).length === 0 && (
        <Card>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('workshop.noMatch', { term: submitted })}
          >
            {/* Deliberately NOT an "add customer" button. Somebody who cannot
                find a customer here should look harder, not create a second
                one — that is what this screen exists to prevent. */}
            <Text type="secondary">{t('workshop.noMatchHelp')}</Text>
          </Empty>
        </Card>
      )}

      {(hits ?? []).map((hit) => (
        <Card
          key={hit.customerUid}
          title={
            <Flex vertical gap={2}>
              <Flex align="center" gap={8} wrap>
                <Text strong style={{ fontSize: 17 }}>
                  {hit.customerName}
                </Text>
                <Tag>{t(`workshop.matchedOn.${hit.matchedOn}`)}</Tag>
              </Flex>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {hit.customerNo} · {orDash(hit.phone)}
              </Text>
            </Flex>
          }
        >
          <Flex vertical gap={12}>
            {hit.openJobs.length > 0 && (
              <Flex vertical gap={6}>
                <Text type="secondary">{t('workshop.alreadyOpen')}</Text>
                {hit.openJobs.map((job) => (
                  <Button
                    key={job.uid}
                    block
                    size="large"
                    onClick={() => {
                      void navigate(`/workshop/job-cards/${job.uid}`);
                    }}
                  >
                    <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                      <Text>{job.jobNo}</Text>
                      <Tag color={jobTone(job.status)} style={{ marginInlineEnd: 0 }}>
                        {JOB_STATUS_LABEL[job.status]}
                      </Tag>
                    </Flex>
                  </Button>
                ))}
              </Flex>
            )}

            {hit.units.length === 0 ? (
              <Text type="secondary">{t('workshop.noBikes')}</Text>
            ) : (
              hit.units.map((unit) => (
                <Card key={unit.uid} size="small">
                  <Flex justify="space-between" align="center" gap={12} wrap>
                    <Flex vertical gap={2}>
                      <Text strong>{orDash(unit.plateNo)}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {orDash(unit.model)} · {unit.chassisNo}
                      </Text>
                      {unit.lastJobNo !== null && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('workshop.lastIn', {
                            jobNo: unit.lastJobNo,
                            date: formatDate(unit.lastJobAt),
                          })}
                        </Text>
                      )}
                    </Flex>
                    <Button
                      type="primary"
                      size="large"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setJobType('SERVICE');
                        setMileage(unit.currentMileage);
                        setComplaint('');
                        setOpening({ hit, unit });
                      }}
                    >
                      {t('workshop.openJobCard')}
                    </Button>
                  </Flex>
                </Card>
              ))
            )}
          </Flex>
        </Card>
      ))}

      <ActionDrawer
        open={opening !== null}
        title={t('workshop.openJobCard')}
        submitLabel={t('workshop.openJobCard')}
        submitting={open.isPending}
        onClose={() => {
          setOpening(null);
        }}
        onSubmit={() => {
          open.mutate();
        }}
        consequence={t('workshop.openConsequence', {
          name: opening?.hit.customerName ?? '',
          plate: orDash(opening?.unit.plateNo),
        })}
      >
        <Form layout="vertical">
          <Form.Item label={t('workshop.fields.jobType')} required>
            <Select
              size="large"
              value={jobType}
              onChange={setJobType}
              options={JOB_TYPES.map((type) => ({ value: type, label: JOB_TYPE_LABEL[type] }))}
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.mileageIn')} help={t('workshop.mileageHelp')}>
            <InputNumber
              min={0}
              max={999999}
              size="large"
              style={{ width: '100%' }}
              value={mileage}
              onChange={setMileage}
              suffix="km"
            />
          </Form.Item>
          <Form.Item label={t('workshop.fields.complaint')} help={t('workshop.complaintHelp')}>
            <Input.TextArea
              rows={3}
              size="large"
              value={complaint}
              onChange={(e) => {
                setComplaint(e.target.value);
              }}
              placeholder={t('workshop.complaintPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </Flex>
  );
}
